'use client';

import { createContext, useContext, useState, useEffect, useCallback, ReactNode } from 'react';
import { authenticatedRequest } from '@/lib/api';
import { toast } from 'sonner';
import { authStorage } from '@/lib/auth';
import { getBookingSettings } from '@/lib/tenantSettings';
import { getApiErrorMessage, getVoucherAddErrorMessage, isSlotUnavailable, SlotUnavailableError, type ApiErrorBody } from '@/lib/api-errors';
import { bookingDateFromApi, bookingTimeFromApi } from '@/lib/time-utils';
import type { ShippingAddress } from '@/lib/validation/shippingAddressFields';
import {
  buildVoucherCartPostageFeeMap,
  computeCartTotals,
  sumOrderPostageDiscounts,
} from '@/lib/cart-postage-utils';

export interface CartItem {
  id: string;
  type: 'SESSION' | 'TREATMENT' | 'PACKAGE' | 'VOUCHER';
  name: string;
  price: number;
  date: string;
  time: string;
  duration?: number;
  currency: string;
  orderId?: string;
  orderNumber?: string;
  // Voucher-specific fields
  voucherType?: 'GIFT_VOUCHER';
  recipientName?: string;
  recipientEmail?: string;
  senderName?: string;
  senderEmail?: string;
  deliveryMethod?: string;
  theme?: string;
  personalMessage?: string;
  postageFee?: number;
  /** Order-level postage metadata (for purchase-page prefill). */
  orderPostageOptionName?: string;
  orderPostageFee?: number;
  // VCH-008: mailing address for physical (By Post) delivery
  shippingAddress?: ShippingAddress;
  /** Linked spa package for PACKAGE bookings — used for voucher eligibility (BO-065). */
  spaPackageId?: string;
}

interface CartContextType {
  items: CartItem[];
  totalAmount: number;
  cartSubtotal: number;
  cartPostageTotal: number;
  itemCount: number;
  isLoading: boolean;
  refreshCart: () => Promise<void>;
  addToCart: (bookingData: {
    sessionTypeId?: string;
    treatmentTypeId?: string;
    packageId?: string;
    date: string;
    time: string;
    arrivalTime?: string; // Optional: Session arrival time
    packageTimeSlot?: string;
    bookingType?: string;
    numberOfParticipants?: number;
    selectedTreatments?: Array<{
      treatmentTypeId: string;
      bookingDate: string;
      timeSlot: string;
    }>;
    spaSessionFollowsTreatment?: boolean;
    healthDeclaration?: {
      answers: Record<string, any>;
      customerRemarks?: string;
      customerSignature?: string;
    };
  }) => Promise<void>;
  addVoucherToCart: (voucherData: {
    packageName: string;
    packageId: string;
    amount: number;
    currency: string;
    recipientName: string;
    recipientEmail?: string;
    senderName: string;
    senderEmail: string;
    message?: string;
    deliveryMethod: string;
    theme?: string;
    postageOptionId?: string;
    postageFee?: number;
    shippingAddress?: ShippingAddress;
    quantity?: number;
  }) => void;
  removeFromCart: (bookingId: string) => Promise<void>;
  clearCart: () => void;
}

const CartContext = createContext<CartContextType | undefined>(undefined);

export function CartProvider({ children }: { children: ReactNode }) {
  const [items, setItems] = useState<CartItem[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const [maxCartItems, setMaxCartItems] = useState<number>(6);

  // Calculate total: item prices + order postage once (not per line)
  const { subtotal: cartSubtotal, postageTotal: cartPostageTotal, total: totalAmount } =
    computeCartTotals(items);
  const itemCount = items.length;

  const transformBookingToCartItem = (booking: any): CartItem => {
    const isPackage = Boolean(booking.spaPackageId || booking.spaPackage);
    const isSession = !isPackage && booking.sessionType !== null;
    const isTreatment = !isPackage && !isSession && booking.treatmentType !== null;

    return {
      id: booking.id,
      type: isSession ? 'SESSION' : isTreatment ? 'TREATMENT' : 'PACKAGE',
      name: isSession
        ? booking.sessionType?.name || 'Session'
        : isTreatment
          ? booking.treatmentType?.name || 'Treatment'
          : booking.spaPackage?.name || 'Package',
      price: booking.totalPrice || 0,
      date: booking.startTime
        ? bookingDateFromApi(booking.startTime)
        : bookingDateFromApi(booking.bookingDate),
      time: bookingTimeFromApi(booking.startTime),
      duration: isSession
        ? booking.sessionType?.durationMinutes
        : isTreatment
          ? booking.treatmentType?.durationMinutes
          : booking.spaPackage?.durationMinutes,
      currency: booking.currency || 'GBP',
      orderId: booking.orderItems?.[0]?.orderId,
      orderNumber: booking.orderItems?.[0]?.order?.orderNumber,
      ...(booking.spaPackageId || booking.spaPackage?.id
        ? { spaPackageId: booking.spaPackageId || booking.spaPackage?.id }
        : {}),
    };
  };

  const transformVoucherToCartItem = (voucher: any, postageFee = 0): CartItem => {
    // Get order info from orderVouchers relation if available
    const orderVoucher = voucher.orderVouchers?.[0];
    const postageDiscount = (orderVoucher?.order?.discounts || []).find(
      (discount: { discountType?: string }) => discount.discountType === 'POSTAGE',
    );
    const packageLabel =
      voucher.spaPackage?.name ||
      voucher.name ||
      voucher.packageDescription ||
      'Gift Voucher';
    return {
      id: voucher.id,
      type: 'VOUCHER',
      name: packageLabel,
      price: voucher.giftValue || voucher.originalPrice || 0,
      date: new Date().toISOString().split('T')[0],
      time: '12:00',
      currency: voucher.currency || 'GBP',
      voucherType: 'GIFT_VOUCHER',
      recipientName: voucher.recipientName,
      recipientEmail: voucher.recipientEmail,
      senderName: voucher.senderName,
      senderEmail: voucher.senderEmail,
      deliveryMethod: voucher.deliveryMethod,
      theme: voucher.giftTheme,
      personalMessage: voucher.personalMessage,
      postageFee: postageFee,
      orderPostageOptionName: postageDiscount?.reason,
      orderPostageFee: postageDiscount?.discountValue,
      shippingAddress: (orderVoucher?.order?.shippingAddress as ShippingAddress | undefined) ?? undefined,
      orderId: orderVoucher?.orderId,
      orderNumber: orderVoucher?.order?.orderNumber
    };
  };

  const refreshCart = useCallback(async () => {
    // Check if user is authenticated before fetching cart
    const user = authStorage.getUser();
    if (!user) {
      // User not authenticated, skip cart fetch but preserve client-side items
      return;
    }

    setIsLoading(true);
    try {
      // Fetch PENDING bookings and PENDING vouchers in parallel
      // Use Promise.allSettled so one failure doesn't break the other
      const [bookingsResult, vouchersResult] = await Promise.allSettled([
        authenticatedRequest('/bookings?status=PENDING&limit=50', { method: 'GET' }),
        authenticatedRequest('/vouchers/pending', { method: 'GET' })
      ]);

      const allItems: CartItem[] = [];

      // Process bookings
      if (bookingsResult.status === 'fulfilled' && bookingsResult.value.ok) {
        const data = await bookingsResult.value.json();
        if (data.success && data.data) {
          const bookings = Array.isArray(data.data) ? data.data : [];
          const bookingItems = bookings.map(transformBookingToCartItem);
          allItems.push(...bookingItems);
        }
      } else if (bookingsResult.status === 'rejected') {
        console.warn('Failed to fetch bookings:', bookingsResult.reason);
      }

      // Process vouchers
      if (vouchersResult.status === 'fulfilled' && vouchersResult.value.ok) {
        const data = await vouchersResult.value.json();
        if (data.success && data.data) {
          const vouchers = Array.isArray(data.data) ? data.data : [];
          const postageInputs = vouchers.map((voucher: any) => ({
            id: voucher.id,
            deliveryMethod: voucher.deliveryMethod,
            orderId: voucher.orderVouchers?.[0]?.orderId as string | undefined,
            createdAt: voucher.createdAt as string | undefined,
            orderPostage: sumOrderPostageDiscounts(voucher.orderVouchers?.[0]?.order?.discounts),
          }));
          const postageFeeByVoucherId = buildVoucherCartPostageFeeMap(postageInputs);
          const voucherItems = vouchers.map((voucher: any) =>
            transformVoucherToCartItem(voucher, postageFeeByVoucherId.get(voucher.id) ?? 0),
          );
          allItems.push(...voucherItems);
        }
      } else if (vouchersResult.status === 'rejected') {
        console.warn('Failed to fetch vouchers:', vouchersResult.reason);
      }

      setItems(allItems);
    } catch (error) {
      console.error('Failed to refresh cart:', error);
      setItems([]);
    } finally {
      setIsLoading(false);
    }
  }, []);

  const addToCart = async (bookingData: {
    sessionTypeId?: string;
    treatmentTypeId?: string;
    packageId?: string;
    date: string;
    time: string;
    packageTimeSlot?: string;
    arrivalTime?: string;
    bookingType?: string;
    numberOfParticipants?: number;
    selectedTreatments?: Array<{
      treatmentTypeId: string;
      bookingDate: string;
      timeSlot: string;
    }>;
    spaSessionFollowsTreatment?: boolean;
    healthDeclaration?: {
      answers: Record<string, any>;
      customerRemarks?: string;
      customerSignature?: string;
    };
  }) => {
    // Check cart limit - use tenant settings or default to 6
    if (items.length >= maxCartItems) {
      toast.error(`Maximum ${maxCartItems} services allowed in cart. Remove some items to add more.`);
      throw new Error(`Cart limit reached: maximum ${maxCartItems} items allowed`);
    }

    try {
      let endpoint: string;
      let body: any = {
        date: bookingData.date,
        sourceType: 'WEBSITE'
      };

      if (bookingData.sessionTypeId) {
        endpoint = '/bookings/simplified';
        body.sessionTypeId = bookingData.sessionTypeId;
        body.time = bookingData.time;
        if (bookingData.bookingType) body.bookingType = bookingData.bookingType;
        if (bookingData.numberOfParticipants) body.numberOfParticipants = bookingData.numberOfParticipants;
      } else if (bookingData.treatmentTypeId) {
        endpoint = '/bookings/simplified/treatment';
        body.treatmentTypeId = bookingData.treatmentTypeId;
        body.time = bookingData.time;
        if (bookingData.arrivalTime) body.arrivalTime = bookingData.arrivalTime; // Pass arrival time
        if (bookingData.healthDeclaration) body.healthDeclaration = bookingData.healthDeclaration;
      } else if (bookingData.packageId) {
        endpoint = '/simplified-packages/bookings';
        body.packageId = bookingData.packageId;
        body.packageBookingDate = bookingData.date;
        body.packageTimeSlot = bookingData.packageTimeSlot || bookingData.time;
        if (bookingData.arrivalTime) body.arrivalTime = bookingData.arrivalTime; // Pass arrival time
        if (bookingData.selectedTreatments) body.selectedTreatments = bookingData.selectedTreatments;
        if (bookingData.spaSessionFollowsTreatment !== undefined) {
          body.spaSessionFollowsTreatment = bookingData.spaSessionFollowsTreatment;
        }
        if (bookingData.healthDeclaration) body.healthDeclaration = bookingData.healthDeclaration;
      } else {
        throw new Error('Invalid booking data: must provide sessionTypeId, treatmentTypeId, or packageId');
      }

      const response = await authenticatedRequest(endpoint, {
        method: 'POST',
        body: JSON.stringify(body)
      });

      if (!response.ok) {
        // The api client resolves (not throws) on non-2xx and preserves the JSON body.
        // dev merge: `error.error` can be a plain string OR the structured {code, message}
        // shape depending on the endpoint — getApiErrorMessage handles both.
        const errorBody = (await response.json().catch(() => null)) as ApiErrorBody | null;
        const message = getApiErrorMessage(errorBody, 'Failed to add to cart');
        // BO-012 AC3: a 409 slot conflict carries suggested alternative times — surface
        // them to the caller (the cart page opens a picker) rather than a dead-end toast.
        if (isSlotUnavailable(response.status, errorBody)) {
          const alternatives = typeof errorBody?.error === 'object' ? errorBody.error.alternatives ?? [] : [];
          throw new SlotUnavailableError(message, alternatives);
        }
        throw new Error(message);
      }

      const result = await response.json();

      if (result.success) {
        // BO-028: append auto-allocation assignment to the success toast for treatment bookings
        const assignment = result.data?.assignment;
        const practitionerName =
          assignment?.practitioner?.name ||
          (assignment?.practitioner?.firstName
            ? `${assignment.practitioner.firstName}${assignment.practitioner.lastName ? ` ${assignment.practitioner.lastName}` : ''}`
            : null);
        const roomName = assignment?.room?.name;
        const allocationSuffix =
          practitionerName
            ? ` · Therapist: ${practitionerName}${roomName ? ` · Room: ${roomName}` : ''}`
            : '';
        toast.success((result.message || 'Added to cart') + allocationSuffix);
        await refreshCart();
        return result.data;
      } else {
        throw new Error(getApiErrorMessage(result, 'Failed to add to cart'));
      }
    } catch (error: any) {
      console.error('Error adding to cart:', error);
      // Every error (SlotUnavailableError or not) propagates to the caller without
      // toasting here — addToCart has exactly one caller (cart-page.tsx
      // processAddToCart), which already decides toast-vs-dialog per error type.
      // Toasting in both layers double-displayed the same message for any
      // non-SlotUnavailableError failure.
      throw error;
    }
  };

  const addVoucherToCart = async (voucherData: {
    packageName: string;
    packageId: string;
    amount: number;
    currency: string;
    recipientName: string;
    recipientEmail?: string;
    senderName: string;
    senderEmail: string;
    message?: string;
    deliveryMethod: string;
    theme?: string;
    postageOptionId?: string;
    postageFee?: number;
    shippingAddress?: ShippingAddress;
    quantity?: number;
  }) => {
    const quantity = voucherData.quantity || 1;

    // Check cart limit
    if (items.length + quantity > maxCartItems) {
      toast.error(`Maximum ${maxCartItems} items allowed in cart. Current cart: ${items.length}, trying to add: ${quantity}.`);
      throw new Error(`Cart limit reached: maximum ${maxCartItems} items allowed`);
    }

    try {
      setIsLoading(true);

      // Create multiple vouchers if quantity > 1
      const vouchersToCreate = Array.from({ length: quantity }, (_, index) => {
        // Only include postage on the first voucher
        const isFirstVoucher = index === 0;

        return {
          sessionTypeId: voucherData.packageId, // For vouchers, this is the package/service ID
          // NO date, time - this tells backend it's a voucher
          recipientName: voucherData.recipientName,
          senderName: voucherData.senderName,
          senderEmail: voucherData.senderEmail,
          recipientEmail: voucherData.recipientEmail,
          giftMessage: voucherData.message,
          giftTheme: voucherData.theme,
          // deliveryMethod is already converted to 'ELECTRONIC' or 'PHYSICAL' by the purchase page
          deliveryMethod: voucherData.deliveryMethod,
          // VCH-008: unlike the postage option, the mailing address goes on EVERY
          // request. The backend reuses a per-customer/day PENDING order (the
          // write is idempotent for the identical address), but a later purchase
          // after payment mints a FRESH order that would otherwise lack it.
          ...(voucherData.deliveryMethod === 'PHYSICAL' && voucherData.shippingAddress && {
            shippingAddress: voucherData.shippingAddress
          }),
          // Only include postage option on first voucher
          ...(isFirstVoucher && voucherData.postageOptionId && { postageOptionId: voucherData.postageOptionId })
        };
      });

      // Create vouchers sequentially to avoid barcode collision
      // When creating in parallel, the sequence number generation can cause duplicates
      const results = [];
      let sharedOrderId: string | undefined;
      for (const requestBody of vouchersToCreate) {
        const response = await authenticatedRequest('/bookings/simplified', {
          method: 'POST',
          body: JSON.stringify({
            ...requestBody,
            ...(sharedOrderId ? { targetOrderId: sharedOrderId } : {}),
          })
        });

        if (!response.ok) {
          const error = (await response.json().catch(() => null)) as ApiErrorBody | null;
          console.error('Backend error response:', error);
          // BO-047 R2: code-aware message (e.g. the 3-distinct-types limit)
          throw new Error(getVoucherAddErrorMessage(error, 'Failed to add voucher to cart'));
        }

        const payload = (await response.json().catch(() => null)) as {
          data?: { order?: { id?: string } };
        } | null;
        if (payload?.data?.order?.id) {
          sharedOrderId = payload.data.order.id;
        }

        results.push(response);
      }

      // Refresh cart to get the new vouchers with real IDs
      await refreshCart();

      toast.success(`Added ${quantity} voucher${quantity > 1 ? 's' : ''} to cart`);
      return results;
    } catch (error: any) {
      console.error('Error adding voucher to cart:', error);
      toast.error(error.message || 'Failed to add voucher to cart');
      throw error;
    } finally {
      setIsLoading(false);
    }
  };

  const removeFromCart = async (bookingId: string) => {
    try {
      // Determine which endpoint to use based on item type
      const item = items.find(i => i.id === bookingId);
      if (!item) {
        throw new Error('Item not found in cart');
      }

      // For vouchers, use SAME /bookings/simplified/:id endpoint as bookings
      // Backend will detect it's a voucher by checking the item
      let endpoint: string;
      if (item.type === 'VOUCHER') {
        endpoint = `/bookings/simplified/${bookingId}`;
      } else if (item.type === 'SESSION') {
        endpoint = `/bookings/simplified/${bookingId}`;
      } else if (item.type === 'TREATMENT') {
        endpoint = `/bookings/simplified/treatment/${bookingId}`;
      } else {
        endpoint = `/simplified-packages/bookings/${bookingId}`;
      }

      const response = await authenticatedRequest(endpoint, {
        method: 'DELETE'
      });

      if (!response.ok) {
        const error = (await response.json().catch(() => null)) as ApiErrorBody | null;
        throw new Error(getApiErrorMessage(error, 'Failed to remove from cart'));
      }

      toast.success('Removed from cart');
      await refreshCart();
    } catch (error: any) {
      console.error('Error removing from cart:', error);
      toast.error(error.message || 'Failed to remove from cart');
      throw error;
    }
  };

  const clearCart = () => {
    setItems([]);
  };

  // Load cart on mount
  useEffect(() => {
    void refreshCart();
    // Fetch max cart items from tenant settings
    getBookingSettings().then(settings => {
      if (settings.maxCartItems) {
        setMaxCartItems(settings.maxCartItems);
      }
    }).catch(err => {
      console.error('Failed to fetch booking settings:', err);
    });
  }, [refreshCart]);

  return (
    <CartContext.Provider
      value={{
        items,
        totalAmount,
        cartSubtotal,
        cartPostageTotal,
        itemCount,
        isLoading,
        refreshCart,
        addToCart,
        addVoucherToCart,
        removeFromCart,
        clearCart
      }}
    >
      {children}
    </CartContext.Provider>
  );
}

export function useCart() {
  const context = useContext(CartContext);
  if (!context) {
    throw new Error('useCart must be used within CartProvider');
  }
  return context;
}
